import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BannerCarousel({ banners = [], autoPlayInterval = 4000 }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const total = banners.length;

  const goNext = useCallback(() => {
    if (total <= 1) return;
    setCurrent(prev => (prev + 1) % total);
  }, [total]);

  const goPrev = useCallback(() => {
    if (total <= 1) return;
    setCurrent(prev => (prev - 1 + total) % total);
  }, [total]);

  useEffect(() => {
    if (paused || total <= 1) return;
    timerRef.current = setInterval(goNext, autoPlayInterval);
    return () => clearInterval(timerRef.current);
  }, [paused, goNext, autoPlayInterval, total]);

  if (!total) return null;

  return (
    <div
      className="relative w-full overflow-hidden rounded-xl group"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="banner-carousel"
    >
      {/* Slides */}
      <div
        className="flex transition-transform duration-500 ease-out"
        style={{ transform: `translateX(-${current * 100}%)` }}
      >
        {banners.map((b, i) => (
          <div key={b.id || i} className="min-w-full" data-testid={`banner-slide-${i}`}>
            {b.link_url ? (
              <a href={b.link_url} target="_blank" rel="noopener noreferrer">
                <img
                  src={`${API_URL}${b.image_url}`}
                  alt={b.title || 'Banner'}
                  className="w-full h-36 sm:h-48 md:h-56 object-cover rounded-xl"
                  loading="lazy"
                />
              </a>
            ) : (
              <img
                src={`${API_URL}${b.image_url}`}
                alt={b.title || 'Banner'}
                className="w-full h-36 sm:h-48 md:h-56 object-cover rounded-xl"
                loading="lazy"
              />
            )}
            {b.title && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent rounded-b-xl px-4 py-3">
                <p className="text-white text-sm font-medium">{b.title}</p>
                {b.vendor_name && <p className="text-white/70 text-xs">by {b.vendor_name}</p>}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Arrows */}
      {total > 1 && (
        <>
          <button
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
            data-testid="banner-prev"
          >
            <ChevronLeft className="w-4 h-4 text-slate-700" />
          </button>
          <button
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
            data-testid="banner-next"
          >
            <ChevronRight className="w-4 h-4 text-slate-700" />
          </button>
        </>
      )}

      {/* Dots */}
      {total > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {banners.map((_, i) => (
            <button
              key={`dot-${i}`}
              onClick={() => setCurrent(i)}
              className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-white w-5' : 'bg-white/50'}`}
              data-testid={`banner-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
